import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Command } from 'cmdk';
import {
  Home, Target, Network, DollarSign, Briefcase, Globe,
  Shield, FileText, Download, Settings, LayoutGrid,
  Building, Book, Activity, LogOut, Search, Moon, Sun
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';

const CommandPalette = () => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const { theme, toggleTheme } = useTheme();

  // Keyboard shortcut: Cmd/Ctrl + K
  useEffect(() => {
    const down = (e) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  const handleSelect = useCallback((callback) => {
    setOpen(false);
    callback();
  }, []);

  // Navigation commands
  const navigationCommands = [
    { name: 'Executive Dashboard', icon: Home, action: () => navigate('/'), keywords: ['home', 'dashboard', 'executive'] },
    { name: 'DSAT Targets', icon: Target, action: () => navigate('/planning/dsat'), keywords: ['planning', 'dsat', 'targets'] },
    { name: 'Governance', icon: Shield, action: () => navigate('/planning/governance'), keywords: ['planning', 'governance', 'sla'] },
    { name: 'Network Operations', icon: Network, action: () => navigate('/network'), keywords: ['network', 'stations', 'ops'] },
    { name: 'Operations Dashboard', icon: Activity, action: () => navigate('/ops/dashboard'), keywords: ['operations', 'incidents'] },
    { name: 'Revenue', icon: DollarSign, action: () => navigate('/revenue'), keywords: ['revenue', 'finance', 'money'] },
    { name: 'Fleet Dashboard', icon: Briefcase, action: () => navigate('/fleet/dashboard'), keywords: ['fleet', 'vehicles'] },
    { name: 'Analytics', icon: LayoutGrid, action: () => navigate('/analytics'), keywords: ['analytics', 'insights', 'reports'] },
    { name: 'Network Health Map', icon: Globe, action: () => navigate('/network/health-map'), keywords: ['map', 'health', 'geographic'] },
    { name: 'Exports Center', icon: Download, action: () => navigate('/exports'), keywords: ['export', 'download', 'csv'] },
    { name: 'Master Workbook', icon: Book, action: () => navigate('/workbook'), keywords: ['workbook', 'data', 'reference'] },
    { name: 'Admin Settings', icon: Settings, action: () => navigate('/admin'), keywords: ['admin', 'settings', 'config'] },
    { name: 'User Management', icon: Settings, action: () => navigate('/admin/users'), keywords: ['users', 'rbac', 'permissions'] },
  ];

  // Action commands
  const actionCommands = [
    {
      name: `Switch to ${theme === 'dark' ? 'Light' : 'Dark'} Mode`,
      icon: theme === 'dark' ? Sun : Moon,
      action: toggleTheme,
      keywords: ['theme', 'dark', 'light', 'mode']
    },
    {
      name: 'Logout',
      icon: LogOut,
      action: logout,
      keywords: ['logout', 'signout', 'exit']
    },
  ];

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[9999] bg-black/50 backdrop-blur-sm">
      <div className="fixed left-1/2 top-[20%] -translate-x-1/2 w-full max-w-2xl">
        <Command
          className="rounded-lg border shadow-2xl"
          style={{
            backgroundColor: 'var(--bg-primary)',
            borderColor: 'var(--border-primary)',
          }}
        >
          <div className="flex items-center border-b" style={{ borderColor: 'var(--border-primary)' }}>
            <Search className="ml-4" size={20} style={{ color: 'var(--text-tertiary)' }} />
            <Command.Input
              value={search}
              onValueChange={setSearch}
              placeholder="Type a command or search..."
              className="h-14 w-full border-0 bg-transparent px-4 text-base outline-none"
              style={{ color: 'var(--text-primary)' }}
              autoFocus
            />
            <div className="mr-4 flex items-center gap-2">
              <kbd className="px-2 py-1 text-xs rounded"
                style={{
                  backgroundColor: 'var(--bg-tertiary)',
                  color: 'var(--text-secondary)'
                }}
              >
                ESC
              </kbd>
            </div>
          </div>

          <Command.List className="max-h-96 overflow-y-auto p-2">
            <Command.Empty
              className="py-6 text-center text-sm"
              style={{ color: 'var(--text-tertiary)' }}
            >
              No results found.
            </Command.Empty>

            <Command.Group
              heading="Navigation"
              className="px-2 py-1.5 text-xs font-semibold"
              style={{ color: 'var(--text-secondary)' }}
            >
              {navigationCommands.map((cmd) => (
                <Command.Item
                  key={cmd.name}
                  onSelect={() => handleSelect(cmd.action)}
                  className="flex items-center gap-3 rounded-md px-3 py-2.5 cursor-pointer mb-1"
                  style={{
                    transition: 'all 150ms ease-in-out',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--bg-hover)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <cmd.icon size={18} style={{ color: 'var(--text-tertiary)' }} />
                  <span style={{ color: 'var(--text-primary)' }}>{cmd.name}</span>
                </Command.Item>
              ))}
            </Command.Group>

            <Command.Separator className="my-2 h-px" style={{ backgroundColor: 'var(--border-primary)' }} />

            <Command.Group
              heading="Actions"
              className="px-2 py-1.5 text-xs font-semibold"
              style={{ color: 'var(--text-secondary)' }}
            >
              {actionCommands.map((cmd) => (
                <Command.Item
                  key={cmd.name}
                  onSelect={() => handleSelect(cmd.action)}
                  className="flex items-center gap-3 rounded-md px-3 py-2.5 cursor-pointer mb-1"
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--bg-hover)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <cmd.icon size={18} style={{ color: 'var(--text-tertiary)' }} />
                  <span style={{ color: 'var(--text-primary)' }}>{cmd.name}</span>
                </Command.Item>
              ))}
            </Command.Group>

            {user && (
              <>
                <Command.Separator className="my-2 h-px" style={{ backgroundColor: 'var(--border-primary)' }} />
                <div className="px-4 py-2 text-xs" style={{ color: 'var(--text-tertiary)' }}>
                  Logged in as <span style={{ color: 'var(--text-secondary)' }}>{user.email}</span>
                </div>
              </>
            )}
          </Command.List>
        </Command>

        <div className="mt-2 text-center text-xs" style={{ color: 'var(--text-tertiary)' }}>
          Press <kbd className="px-2 py-1 rounded" style={{ backgroundColor: 'var(--bg-tertiary)' }}>⌘K</kbd> to close
        </div>
      </div>

      {/* Click outside to close */}
      <div
        className="absolute inset-0"
        onClick={() => setOpen(false)}
        aria-label="Close command palette"
      />
    </div>
  );
};

export default CommandPalette;